import { AdoptedSSInsertRuleURLBased, AdoptedSSDeleteRule, AdoptedSSAddOwner, TechnicalInfo, } from '../app/messages.gen.js';
import { hasTag } from '../app/guards.js';
import { nextID, styleSheetIDMap } from './constructedStyleSheets.js';
const defaults = {
    checkCssInterval: 200,
    scanInMemoryCSS: false,
    checkLimit: undefined,
};
export default function (app, opts) {
    if (app === null)
        return;
    if (!window.CSSStyleSheet) {
        app.send(TechnicalInfo('no_stylesheet_prototype_in_window', ''));
        return;
    }
    const options = { ...defaults, ...opts };
    //  sheetID:index -> ruleText
    const ruleSnapshots = new Map();
    let checkInterval = null;
    const trackedSheets = new Set();
    const checkIntervalMs = options.checkCssInterval || 200;
    let checkIterations = {};
    function checkRuleChanges() {
        if (!options.scanInMemoryCSS)
            return;
        const allSheets = trackedSheets.values();
        for (const sheet of allSheets) {
            try {
                const sheetID = styleSheetIDMap.get(sheet);
                if (!sheetID)
                    continue;
                if (options.checkLimit) {
                    checkIterations[sheetID] = (checkIterations[sheetID] ?? 0) + 1;
                    if (checkIterations[sheetID] > options.checkLimit) {
                        trackedSheets.delete(sheet);
                        // continue, not return: skipping one exhausted sheet must not
                        // abandon the remaining tracked sheets in this scan.
                        continue;
                    }
                }
                for (let j = 0; j < sheet.cssRules.length; j++) {
                    try {
                        const rule = sheet.cssRules[j];
                        const key = `${sheetID}:${j}`;
                        const oldText = ruleSnapshots.get(key);
                        const newText = rule.cssText;
                        if (oldText !== newText) {
                            if (oldText !== undefined) {
                                // Rule is changed
                                app.send(AdoptedSSDeleteRule(sheetID, j));
                                app.send(AdoptedSSInsertRuleURLBased(sheetID, newText, j, app.getBaseHref()));
                            }
                            else {
                                // Rule added
                                app.send(AdoptedSSInsertRuleURLBased(sheetID, newText, j, app.getBaseHref()));
                            }
                            ruleSnapshots.set(key, newText);
                        }
                    }
                    catch (e) {
                        /* Skip inaccessible rules */
                    }
                }
                const keysToCheck = Array.from(ruleSnapshots.keys()).filter((key) => key.startsWith(`${sheetID}:`));
                for (const key of keysToCheck) {
                    const index = parseInt(key.split(':')[1], 10);
                    if (index >= sheet.cssRules.length) {
                        ruleSnapshots.delete(key);
                    }
                }
            }
            catch (e) {
                /* Skip inaccessible sheets */
                trackedSheets.delete(sheet);
            }
        }
    }
    const emptyRuleReg = /{\s*}/;
    function isRuleEmpty(rule) {
        return emptyRuleReg.test(rule);
    }
    const sendInsertDeleteRule = app.safe((sheet, index, rule) => {
        const sheetID = styleSheetIDMap.get(sheet);
        if (!sheetID)
            return;
        if (typeof rule === 'string') {
            app.send(AdoptedSSInsertRuleURLBased(sheetID, rule, index, app.getBaseHref()));
            if (isRuleEmpty(rule)) {
                ruleSnapshots.set(`${sheetID}:${index}`, rule);
                trackedSheets.add(sheet);
            }
        }
        else {
            app.send(AdoptedSSDeleteRule(sheetID, index));
            if (ruleSnapshots.has(`${sheetID}:${index}`)) {
                ruleSnapshots.delete(`${sheetID}:${index}`);
            }
        }
    });
    const sendReplaceGroupingRule = app.safe((rule) => {
        let topmostRule = rule;
        while (topmostRule.parentRule)
            topmostRule = topmostRule.parentRule;
        const sheet = topmostRule.parentStyleSheet;
        if (!sheet)
            return;
        const sheetID = styleSheetIDMap.get(sheet);
        if (!sheetID)
            return;
        const cssText = topmostRule.cssText;
        const idx = Array.from(sheet.cssRules).indexOf(topmostRule);
        if (idx >= 0) {
            app.send(AdoptedSSInsertRuleURLBased(sheetID, cssText, idx, app.getBaseHref()));
            app.send(AdoptedSSDeleteRule(sheetID, idx + 1));
            if (isRuleEmpty(cssText)) {
                ruleSnapshots.set(`${sheetID}:${idx}`, cssText);
                trackedSheets.add(sheet);
            }
        }
    });
    // Patch prototype methods
    const patchContext = app.safe((context) => {
        if (context.__css_tracking_patched__)
            return;
        context.__css_tracking_patched__ = true;
        const { insertRule, deleteRule } = context.CSSStyleSheet.prototype;
        const { insertRule: groupInsertRule, deleteRule: groupDeleteRule } = context.CSSGroupingRule.prototype;
        context.CSSStyleSheet.prototype.insertRule = function (rule, index = 0) {
            const result = insertRule.call(this, rule, index);
            sendInsertDeleteRule(this, result, rule);
            return result;
        };
        context.CSSStyleSheet.prototype.deleteRule = function (index) {
            sendInsertDeleteRule(this, index);
            return deleteRule.call(this, index);
        };
        context.CSSGroupingRule.prototype.insertRule = function (rule, index = 0) {
            const result = groupInsertRule.call(this, rule, index);
            sendReplaceGroupingRule(this);
            return result;
        };
        context.CSSGroupingRule.prototype.deleteRule = function (index) {
            const result = groupDeleteRule.call(this, index);
            sendReplaceGroupingRule(this);
            return result;
        };
    });
    patchContext(window);
    app.observer.attachContextCallback(patchContext);
    app.nodes.attachNodeCallback((node) => {
        if (!hasTag(node, 'style') || !node.sheet)
            return;
        if (node.textContent !== null && node.textContent.trim().length > 0)
            return;
        const nodeID = app.nodes.getID(node);
        if (!nodeID)
            return;
        const sheet = node.sheet;
        // Accessing cssRules on a cross-origin stylesheet (e.g. injected by a
        // browser extension) throws a SecurityError. Probe it before registering
        // the sheet so an inaccessible sheet is skipped instead of aborting start.
        let rules;
        try {
            rules = sheet.cssRules;
        }
        catch (e) {
            // Skip inaccessible (cross-origin) stylesheet
            app.debug.log('Couldnt access stylesheet during initial scan', e);
            return;
        }
        const sheetID = nextID();
        styleSheetIDMap.set(sheet, sheetID);
        app.send(AdoptedSSAddOwner(sheetID, nodeID));
        for (let i = 0; i < rules.length; i++) {
            try {
                sendInsertDeleteRule(sheet, i, rules[i].cssText);
            }
            catch (e) {
                // Skip inaccessible rules
                app.debug.log('Couldnt access stylesheet rule during initial scan', e);
            }
        }
    });
    function startChecking() {
        if (checkInterval || !options.scanInMemoryCSS)
            return;
        checkInterval = window.setInterval(checkRuleChanges, checkIntervalMs);
    }
    setTimeout(startChecking, 50);
    app.attachStopCallback(() => {
        if (checkInterval) {
            clearInterval(checkInterval);
            checkInterval = null;
        }
        ruleSnapshots.clear();
    });
}
